import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Button,
  Card,
  SectionHeader,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createInsightsApi } from "@/modules/dashboard/insights/api";
import {
  EnvBanner,
  HBars,
  Note,
  Stat,
  StatGrid,
  TwoCol,
} from "@/modules/dashboard/insights/charts";
import {
  formatAgo,
  formatBytes,
  formatDateTime,
  formatLabel,
  formatNumber,
} from "@/modules/dashboard/insights/format";
import { InsightBoundary } from "@/modules/dashboard/insights/parts";
import type { SystemDto } from "@/modules/dashboard/insights/types";
import { useInsight } from "@/modules/dashboard/insights/useInsight";
import { buildHealthChecks, worstStatus, type HealthStatus } from "../health";
import { SystemOperationsPanel } from "./SystemQueuePages";

const STATUS_TONE: Record<HealthStatus, "success" | "warning" | "danger" | "neutral"> = {
  ok: "success",
  warn: "warning",
  bad: "danger",
  unknown: "neutral",
};

const STATUS_TEXT: Record<HealthStatus, string> = {
  ok: "Healthy",
  warn: "Check",
  bad: "Problem",
  unknown: "Unknown",
};

/**
 * System: is the machinery behind HUDDL working? Which database this is,
 * background jobs, queues, webhooks, storage and configuration — plus the
 * existing maintenance, flags, access and audit screens on the second tab.
 */
export function SystemHubPage() {
  const client = useApiClient();
  const api = useMemo(() => createInsightsApi(client), [client]);
  const [tab, setTab] = useState("health");

  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "System" }]);

  const state = useInsight(() => api.system(), [api]);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="System"
        description="Is the machinery behind HUDDL working? Database, background jobs, queues, payments webhooks, storage and configuration. Secrets are never shown, only whether they are set."
        actions={
          tab === "health" ? (
            <Button variant="secondary" onClick={state.reload} loading={state.loading}>
              Refresh
            </Button>
          ) : null
        }
      />

      <Tabs
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: "health",
            label: "Health",
            content: (
              <InsightBoundary state={state} what="system health">
                {(s) => <HealthTab s={s} />}
              </InsightBoundary>
            ),
          },
          { id: "ops", label: "Operations and governance", content: <SystemOperationsPanel /> },
        ]}
      />
    </Stack>
  );
}

function HealthTab({ s }: { s: SystemDto }) {
  const navigate = useNavigate();
  const checks = useMemo(() => buildHealthChecks(s), [s]);
  const overall = worstStatus(checks);
  const counts = checks.reduce<Record<HealthStatus, number>>(
    (acc, c) => ({ ...acc, [c.status]: acc[c.status] + 1 }),
    { ok: 0, warn: 0, bad: 0, unknown: 0 },
  );
  const env = s.config.environment;

  return (
    <Stack gap={24}>
      <EnvBanner kind={env.kind} label={env.label} projectRef={env.supabase_project_ref} />

      <Card
        header={
          <Flex justify="space-between" align="center" wrap>
            <Flex gap={8} align="center">
              <Text role="title">Checks</Text>
              <StatusChip tone={STATUS_TONE[overall]}>{STATUS_TEXT[overall]}</StatusChip>
            </Flex>
            <Text role="small" color="secondary">
              {counts.bad} problems · {counts.warn} to check · {counts.unknown} unknown · {counts.ok} healthy · checked{" "}
              {formatAgo(s.generated_at)}
            </Text>
          </Flex>
        }
      >
        <div style={{ overflowX: "auto" }}>
          <Table>
            <Thead>
              <Tr>
                <Th>Area</Th>
                <Th>Check</Th>
                <Th>Status</Th>
                <Th>What was seen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {checks.map((c) => (
                <Tr
                  key={c.id}
                  onClick={c.href ? () => navigate(c.href!) : undefined}
                  style={c.href ? { cursor: "pointer" } : undefined}
                >
                  <Td>{c.area}</Td>
                  <Td>{c.label}</Td>
                  <Td>
                    <StatusChip tone={STATUS_TONE[c.status]}>{STATUS_TEXT[c.status]}</StatusChip>
                  </Td>
                  <Td>{c.detail}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </Card>

      <Card header={<Text role="title">Background jobs</Text>}>
        {s.worker_heartbeats.length === 0 ? (
          <Note>
            No background job has reported yet. The scheduled job runs every minute and writes one heartbeat per job once
            the API build that includes heartbeats is deployed and migration i49 is applied to this database.
          </Note>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <Thead>
                <Tr>
                  <Th>Job</Th>
                  <Th>Last run</Th>
                  <Th>Last success</Th>
                  <Th>Last run processed</Th>
                  <Th>Runs / failed runs</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {s.worker_heartbeats.map((w) => {
                  const status: HealthStatus = w.quiet ? "bad" : w.last_error || w.last_failed > 0 ? "warn" : "ok";
                  return (
                    <Tr key={w.worker}>
                      <Td>{formatLabel(w.worker)}</Td>
                      <Td>{formatAgo(w.last_run_at)}</Td>
                      <Td>{formatAgo(w.last_success_at)}</Td>
                      <Td>
                        {formatNumber(w.last_processed)} ({formatNumber(w.last_failed)} failed)
                        {w.last_error ? (
                          <Text role="small" color="secondary" as="div">
                            {w.last_error}
                          </Text>
                        ) : null}
                      </Td>
                      <Td>
                        {formatNumber(w.run_count)} / {formatNumber(w.failure_count)}
                      </Td>
                      <Td>
                        <StatusChip tone={STATUS_TONE[status]}>
                          {w.quiet ? "Not reporting" : STATUS_TEXT[status]}
                        </StatusChip>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Queues</Text>}>
          <StatGrid>
            <Stat
              label="Media waiting for deletion"
              value={formatNumber(s.media_deletions.depth)}
              note={s.media_deletions.oldest_at ? `Oldest since ${formatDateTime(s.media_deletions.oldest_at)}` : "Empty"}
            />
            <Stat
              label="Domain events waiting"
              value={formatNumber(s.domain_events.undelivered)}
              note={`${formatNumber(s.domain_events.failed)} failed · ${formatNumber(s.domain_events.quarantined)} quarantined`}
            />
            <Stat
              label="Integration events pending"
              value={formatNumber(s.integration_events.pending)}
              note={`${formatNumber(s.integration_events.stuck_processing)} stuck processing`}
            />
            <Stat
              label="Notification deliveries pending"
              value={formatNumber(s.notification_delivery.pending)}
              note={`${formatNumber(s.notification_delivery.failed)} failed · ${formatNumber(s.notification_delivery.retrying)} retrying`}
            />
            <Stat label="Live booking holds" value={formatNumber(s.holds.live)} note={`${formatNumber(s.holds.overdue)} overdue`} />
            <Stat
              label="Requests in progress"
              value={formatNumber(s.idempotency.in_progress)}
              note={`${formatNumber(s.idempotency.stuck_in_progress)} older than 10 minutes`}
            />
          </StatGrid>
        </Card>

        <Card header={<Text role="title">Payments and webhooks</Text>}>
          <Stack gap={12}>
            <StatGrid>
              <Stat
                label="Xendit"
                value={s.config.payments.configured ? `Configured (${s.config.payments.mode})` : "Not configured"}
              />
              <Stat label="Webhooks received, 7 days" value={formatNumber(s.ingress.received_7d)} />
              <Stat label="Webhooks rejected, 7 days" value={formatNumber(s.ingress.rejected_7d)} />
              <Stat label="Last webhook" value={formatAgo(s.ingress.last_received_at)} />
            </StatGrid>
            {s.ingress.last_rejection ? (
              <Note>
                Last rejection {formatDateTime(s.ingress.last_rejection.at)} ({s.ingress.last_rejection.integration_type}):{" "}
                {s.ingress.last_rejection.reason ?? "no reason recorded"}
              </Note>
            ) : null}
            {s.ingress.by_type.length === 0 ? (
              <Note>No provider webhook has ever reached this API. The first appears when a player pays through Xendit.</Note>
            ) : (
              <HBars
                items={s.ingress.by_type.map((r) => ({
                  label: `${r.integration_type} · ${formatLabel(r.status)}`,
                  value: r.n,
                }))}
              />
            )}
          </Stack>
        </Card>
      </TwoCol>

      <Card header={<Text role="title">Recent failures</Text>}>
        {s.recent_failures.length === 0 ? (
          <Note>No failed or quarantined domain or integration event is on record.</Note>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <Thead>
                <Tr>
                  <Th>When</Th>
                  <Th>Source</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Reason</Th>
                </Tr>
              </Thead>
              <Tbody>
                {s.recent_failures.map((f) => (
                  <Tr
                    key={`${f.source}-${f.id}`}
                    onClick={
                      f.source === "integration_event" ? () => navigate(`/app/system/integration-events/${f.id}`) : undefined
                    }
                    style={f.source === "integration_event" ? { cursor: "pointer" } : undefined}
                  >
                    <Td>{formatDateTime(f.at)}</Td>
                    <Td>{formatLabel(f.source)}</Td>
                    <Td>{f.type}</Td>
                    <Td>{formatLabel(f.status)}</Td>
                    <Td>{f.reason ?? "Not recorded"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </Card>

      <TwoCol>
        <Card header={<Text role="title">Database</Text>}>
          <Stack gap={12}>
            <StatGrid>
              <Stat label="Size" value={formatBytes(s.database.database_bytes)} />
              <Stat label="Open connections" value={formatNumber(s.database.connections)} />
              <Stat label="Tables" value={formatNumber(s.database.public_tables)} />
              <Stat label="Round trip from API" value={`${formatNumber(s.api.db_round_trip_ms)} ms`} />
            </StatGrid>
            <HBars
              items={s.database.largest_tables.map((t) => ({
                label: t.table,
                value: t.bytes,
                display: `${formatBytes(t.bytes)} · ~${formatNumber(t.approx_rows)} rows`,
              }))}
            />
            <Note>
              Migration ledger: {formatNumber(s.migration_ledger.count)} entries, latest {s.migration_ledger.latest ?? "none"}.
              The ledger is known to lag the live schema (migrations are applied by hand), so the schema check above looks
              for the tables recent migrations create instead of trusting it.
            </Note>
          </Stack>
        </Card>

        <Card header={<Text role="title">Storage and configuration</Text>}>
          <Stack gap={12}>
            <StatGrid>
              <Stat label="Storage provider" value={s.config.storage.provider.toUpperCase()} />
              <Stat label="File records" value={formatNumber(s.files.total)} note={formatBytes(s.files.total_bytes)} />
              <Stat label="API audience default" value={s.config.api.audience} />
              <Stat label="Allowed browser origins" value={formatNumber(s.config.api.cors_origin_count)} />
            </StatGrid>
            <Table>
              <Thead>
                <Tr>
                  <Th>Purpose</Th>
                  <Th>Bucket</Th>
                  <Th>Access</Th>
                </Tr>
              </Thead>
              <Tbody>
                {s.config.storage.buckets.map((b) => (
                  <Tr key={b.purpose}>
                    <Td>{b.purpose}</Td>
                    <Td>{b.name}</Td>
                    <Td>
                      {b.private ? (
                        <StatusChip tone="info">Private, signed links only</StatusChip>
                      ) : (
                        <StatusChip tone="neutral">{b.public_base_url_set ? "Public via CDN" : "Public"}</StatusChip>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <HBars
              empty="No file records."
              items={Object.entries(s.files.by_classification).map(([k, v]) => ({ label: formatLabel(k), value: v }))}
            />
          </Stack>
        </Card>
      </TwoCol>
    </Stack>
  );
}
